import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { SessionQueryProvider } from './auth/SessionQueryProvider';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import * as Sentry from '@sentry/react';

import App from './App';
import { initI18n } from './i18n/init';
import { scrubSensitiveUrl } from './lib/analyticsSanitization';
import { initOpenPanel } from './lib/openpanel';
import { installDevTools } from './lib/devTools';
import { scrubSentryBreadcrumb } from './lib/sentryBreadcrumbs';
import { scrubAuthTokensFromTransaction } from './lib/scrubUrl';
import { initTheme } from './lib/theme';
import { config } from './lib/config';
import './index.css';

// Apply the user's stored theme BEFORE React renders so the first paint
// already shows the right palette — otherwise the page briefly flashes
// the default tokens before the React tree mounts and switches.
initTheme();
initI18n();
initOpenPanel();
installDevTools();

if (config.SENTRY_DSN) {
  Sentry.init({
    dsn: config.SENTRY_DSN,
    tunnel: config.SENTRY_TUNNEL || undefined,
    environment: config.IA_ENV,
    release: config.RELEASE,
    tracesSampleRate: 0.1,
    integrations: [Sentry.browserTracingIntegration()],
    beforeSend(event) {
      // OWASP A02 — strip request bodies/responses to avoid forwarding PII to Sentry.
      if (event.request) {
        delete event.request.data;
        // Query strings can carry emails (password reset) and magic-link
        // tokens; path segments can embed patient ids — scrub both before
        // the event leaves the browser.
        if (event.request.url) {
          event.request.url = scrubSensitiveUrl(event.request.url);
        }
        if (event.request.query_string) {
          delete event.request.query_string;
        }
      }
      return event;
    },
    // Pageload transactions snapshot the landing URL before Login's
    // replaceState drops `?secureToken=`. Scrub the name, request URL,
    // and span URLs here so legacy query-string emails cannot ship the token
    // (or patient ids) to Sentry (FOOD-1293).
    beforeSendTransaction(event) {
      scrubAuthTokensFromTransaction(event);
      return event;
    },
    beforeBreadcrumb(breadcrumb) {
      // Scrub URL fields on xhr/fetch/navigation; leave free-text messages
      // on path-id parameterization only (see scrubSentryBreadcrumb).
      // Also strips magic-link tokens from message text when present.
      return scrubSentryBreadcrumb(breadcrumb);
    },
  });
}

const Root = () => (
  <Sentry.ErrorBoundary fallback={<div className="p-8">Something went wrong.</div>}>
    {/* Opt into React Router v7 behaviors so the v6 deprecation warnings
        (`v7_startTransition`, `v7_relativeSplatPath`) stop logging on
        every render. We're already compatible with both behaviors —
        this just silences the noise without changing app behavior. */}
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <SessionQueryProvider>
        <App />
        <ReactQueryDevtools initialIsOpen={false} />
      </SessionQueryProvider>
    </BrowserRouter>
  </Sentry.ErrorBoundary>
);

// Vite's HMR can re-execute main.tsx during a hot update, which calls
// `createRoot` a second time on the same DOM node — React 18 logs:
//   "You are calling ReactDOMClient.createRoot() on a container that has
//    already been passed to createRoot() before."
// Stash the root on the container so subsequent runs reuse it.
const container = document.getElementById('root')! as HTMLElement & {
  __rxRoot?: ReactDOM.Root;
};
const root = container.__rxRoot ?? ReactDOM.createRoot(container);
container.__rxRoot = root;
root.render(
  <React.StrictMode>
    <Root />
  </React.StrictMode>,
);
